import { createReducer, Reducer } from "@reduxjs/toolkit";
import { Pageable } from "../../services/pageable";
import { RemoteResource } from "../common/fetchable";
import { EmptyFetchable, Fetchable } from "../common/fetchable/helpers";
import { BillingActions } from "./actions";
import { BillingDTO } from "./models/dto";

export namespace Billing {
  export const pricing: RemoteResource<
    BillingDTO.Responses.GetPricing.T,
    BillingDTO.Responses.GetPricing.T
  > = {
    fetch: BillingActions.getPricing,
    updateStore: BillingActions.setPricing,
    selector: (store) => store.billing.pricing,
  };

  export const balance: RemoteResource<
    BillingDTO.Responses.GetBalance.T,
    BillingDTO.Responses.GetBalance.T
  > = {
    fetch: BillingActions.getBalance,
    updateStore: BillingActions.setBalance,
    selector: (store) => store.billing.balance,
  };

  export const chainBilling: RemoteResource<
    BillingDTO.Responses.GetBillingInfo.T,
    BillingDTO.Responses.GetBillingInfo.T
  > = {
    fetch: BillingActions.getChainBilling,
    updateStore: BillingActions.setChainBilling,
    selector: (store) => store.billing.chainBilling,
  };

  export const vaultsBilling: RemoteResource<
    BillingDTO.Responses.GetBillingInfo.T,
    BillingDTO.Responses.GetBillingInfo.T
  > = {
    fetch: BillingActions.getVaultsBilling,
    updateStore: BillingActions.setVaultsBilling,
    selector: (store) => store.billing.vaultsBilling,
  };

  export const verificationBilling: RemoteResource<
    BillingDTO.Responses.GetBillingInfo.T,
    BillingDTO.Responses.GetBillingInfo.T
  > = {
    fetch: BillingActions.getVerificationBilling,
    updateStore: BillingActions.setVerificationBilling,
    selector: (store) => store.billing.verificationBilling,
  };

  export const forexRate: RemoteResource<
    BillingDTO.Responses.GetForexRate.T,
    BillingDTO.Responses.GetForexRate.T
  > = {
    fetch: BillingActions.getForexRate,
    updateStore: BillingActions.setForexRate,
    selector: (store) => store.billing.forexRate,
  };

  export const skrtAddresses: RemoteResource<
    BillingDTO.Responses.GetSKRTAddresses.T,
    BillingDTO.Responses.GetSKRTAddresses.T
  > = {
    fetch: BillingActions.getSKRTAddresses,
    updateStore: BillingActions.setSKRTAddresses,
    selector: (store) => store.billing.skrtAddresses,
  };

  export const allTransactions: RemoteResource<
    BillingDTO.Responses.GetAllTransactions.T,
    BillingDTO.Responses.GetAllTransactions.T,
    Pageable.T<Exclude<keyof BillingDTO.Responses.GetSingleTransaction.T, "id">>
  > = {
    fetch: BillingActions.setAllTransactionsPagination,
    updateStore: BillingActions.setAllTransactions,
    selector: (store) => store.billing.allTransactions.data,
  };

  export const allUsages: RemoteResource<
    BillingDTO.Responses.GetAllUsages.T,
    BillingDTO.Responses.GetAllUsages.T,
    Pageable.T<
      Exclude<
        keyof BillingDTO.Responses.GetSingleUsage.T,
        "id" | "featureNames"
      >
    >
  > = {
    fetch: BillingActions.setAllUsagesPagination,
    updateStore: BillingActions.setAllUsages,
    selector: (store) => store.billing.allUsages.data,
  };
}

/**
 * Interface used for Redux Store
 */
export interface Billing {
  pricing: Fetchable<BillingDTO.Responses.GetPricing.T>;
  balance: Fetchable<BillingDTO.Responses.GetBalance.T>;
  chainBilling: Fetchable<BillingDTO.Responses.GetBillingInfo.T>;
  vaultsBilling: Fetchable<BillingDTO.Responses.GetBillingInfo.T>;
  verificationBilling: Fetchable<BillingDTO.Responses.GetBillingInfo.T>;
  forexRate: Fetchable<BillingDTO.Responses.GetForexRate.T>;
  skrtAddresses: Fetchable<BillingDTO.Responses.GetSKRTAddresses.T>;
  allTransactions: {
    data: Fetchable<BillingDTO.Responses.GetAllTransactions.T>;
    pagination: Pageable.T<
      Exclude<keyof BillingDTO.Responses.GetSingleTransaction.T, "id">
    >;
  };
  singleTransactionMap: Record<
    string,
    BillingDTO.Responses.GetSingleTransaction.T | "LOADING"
  >;
  allUsages: {
    data: Fetchable<BillingDTO.Responses.GetAllUsages.T>;
    pagination: Pageable.T<
      Exclude<
        keyof BillingDTO.Responses.GetSingleUsage.T,
        "id" | "featureNames"
      >
    >;
  };
  singleUsageMap: Record<
    string,
    BillingDTO.Responses.GetSingleUsage.T | "LOADING"
  >;
}

const initialState: Billing = {
  pricing: EmptyFetchable,
  balance: EmptyFetchable,
  chainBilling: EmptyFetchable,
  vaultsBilling: EmptyFetchable,
  verificationBilling: EmptyFetchable,
  forexRate: EmptyFetchable,
  skrtAddresses: EmptyFetchable,
  allTransactions: {
    data: EmptyFetchable,
    pagination: {},
  },
  singleTransactionMap: {},
  allUsages: {
    data: EmptyFetchable,
    pagination: {},
  },
  singleUsageMap: {},
};

// Reducer
export const billingReducer: Reducer<
  Billing,
  BillingActions.Actions
> = createReducer(initialState, (builder) => {
  builder
    .addCase(BillingActions.setPricing, (state, { payload }) => {
      state.pricing = payload;
    })
    .addCase(BillingActions.resetPricing, (state) => {
      state.pricing = EmptyFetchable;
    })
    .addCase(BillingActions.setBalance, (state, { payload }) => {
      state.balance = payload;
    })
    .addCase(BillingActions.resetBalance, (state) => {
      state.balance = EmptyFetchable;
    })
    .addCase(BillingActions.resetChainBilling, (state) => {
      state.chainBilling = EmptyFetchable;
    })
    .addCase(BillingActions.getChainBilling, (state) => {
      state.chainBilling = {
        status: "loading",
      };
    })
    .addCase(BillingActions.setChainBilling, (state, action) => {
      state.chainBilling = action.payload;
    })
    .addCase(BillingActions.resetVaultsBilling, (state) => {
      state.vaultsBilling = EmptyFetchable;
    })
    .addCase(BillingActions.getVaultsBilling, (state) => {
      state.vaultsBilling = {
        status: "loading",
      };
    })
    .addCase(BillingActions.setVaultsBilling, (state, action) => {
      state.vaultsBilling = action.payload;
    })
    .addCase(BillingActions.resetVerificationBilling, (state) => {
      state.verificationBilling = EmptyFetchable;
    })
    .addCase(BillingActions.getVerificationBilling, (state) => {
      state.verificationBilling = {
        status: "loading",
      };
    })
    .addCase(BillingActions.setVerificationBilling, (state, action) => {
      state.verificationBilling = action.payload;
    })
    .addCase(BillingActions.resetForexRate, (state) => {
      state.forexRate = EmptyFetchable;
    })
    .addCase(BillingActions.getForexRate, (state) => {
      state.forexRate = {
        status: "loading",
      };
    })
    .addCase(BillingActions.setForexRate, (state, action) => {
      state.forexRate = action.payload;
    })
    .addCase(BillingActions.resetSKRTAddresses, (state) => {
      state.skrtAddresses = EmptyFetchable;
    })
    .addCase(BillingActions.getSKRTAddresses, (state) => {
      state.skrtAddresses = {
        status: "loading",
      };
    })
    .addCase(BillingActions.setSKRTAddresses, (state, action) => {
      state.skrtAddresses = action.payload;
    })
    .addCase(BillingActions.setAllTransactionsPagination, (state, action) => {
      state.allTransactions.pagination = action.payload;
      state.allTransactions.data = {
        status: "loading",
      };
    })
    .addCase(BillingActions.setAllTransactions, (state, action) => {
      state.allTransactions.data = action.payload;
      //TODO: use id as link to map
      if (action.payload.status === "ready") {
        action.payload.data.content.forEach((p) => {
          state.singleTransactionMap[p.id] = p;
        });
      }
    })
    .addCase(BillingActions.resetAllTransactions, (state) => {
      state.allTransactions.data = EmptyFetchable;
      state.allTransactions.pagination = {};
    })
    .addCase(BillingActions.setSingleTransaction, (state, action) => {
      state.singleTransactionMap[action.payload.id] = action.payload.result;
    })
    .addCase(BillingActions.getAllUsages, (state) => {
      state.allUsages.data = {
        status: "loading",
      };
    })
    .addCase(BillingActions.setAllUsagesPagination, (state, action) => {
      state.allUsages.pagination = action.payload;
      state.allUsages.data = {
        status: "loading",
      };
    })
    .addCase(BillingActions.setAllUsages, (state, action) => {
      state.allUsages.data = action.payload;
      //TODO: use id as link to map
      if (action.payload.status === "ready") {
        action.payload.data.content.forEach((p) => {
          state.singleUsageMap[p.id] = p;
        });
      }
    })
    .addCase(BillingActions.resetAllUsages, (state) => {
      state.allUsages.data = EmptyFetchable;
      state.allUsages.pagination = {};
    })
    .addCase(BillingActions.setSingleUsage, (state, action) => {
      state.singleUsageMap[action.payload.id] = action.payload.result;
    });
});
